import { useEffect, useState, type FormEvent } from "react";
import { authHeaders } from "./auth";

/** Riga di GET /api/admin/users (tutti gli account, anche disattivi). */
interface AdminUser {
  id: string;
  name: string;
  department: string;
  role: "technician" | "expert";
  username: string | null;
  active: boolean;
  isAdmin: boolean;
  hasPassword: boolean;
  fromPredimain: boolean;
  createdAt: string;
}

/** Password appena creata o reimpostata: si vede una volta sola, l'amministratore la consegna all'utente. */
interface Issued { title: string; username: string; password: string }

async function call<T>(token: string, url: string, method: string, body?: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url, { method, headers: { ...authHeaders(token), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) }, body: body !== undefined ? JSON.stringify(body) : undefined });
  } catch { throw new Error("Server non raggiungibile"); }
  if (r.status === 403) throw new Error("Riservato agli amministratori");
  if (!r.ok) {
    let msg = `Il server ha risposto ${r.status}`;
    try { const j = (await r.json()) as { error?: string }; if (j.error) msg = j.error; } catch { /* senza corpo */ }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

function roleText(r: AdminUser["role"]): string { return r === "technician" ? "HoloLens" : "Esperto"; }

/**
 * Gestione utenti (02/11/2026): pagina riservata agli amministratori (is_admin). Elenco degli account, nuovo account,
 * attiva/disattiva, amministratore sì/no e nuova password: chi dimentica la password la chiede a un amministratore,
 * che gliela reimposta da qui (non c'è recupero via email: gli account non hanno un indirizzo).
 */
export default function Users({ token, meId, onBack }: { token: string; meId: string; onBack: () => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // id dell'utente su cui si sta lavorando
  const [issued, setIssued] = useState<Issued | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("");

  // nuovo account
  const [nName, setNName] = useState("");
  const [nDept, setNDept] = useState("");
  const [nRole, setNRole] = useState<AdminUser["role"]>("expert");
  const [nUser, setNUser] = useState("");
  const [nPwd, setNPwd] = useState("");
  const [nAdmin, setNAdmin] = useState(false);

  async function reload() {
    setError(null);
    try { setUsers(await call<AdminUser[]>(token, "/api/admin/users", "GET")); } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void reload(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [token]);

  async function act(id: string, fn: () => Promise<void>) {
    setBusy(id); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  function update(id: string, body: { name?: string; department?: string; active?: boolean; isAdmin?: boolean }) {
    return act(id, async () => {
      const u = await call<AdminUser>(token, `/api/admin/users/${encodeURIComponent(id)}`, "PATCH", body);
      setUsers((us) => (us ?? []).map((x) => (x.id === id ? u : x)));
    });
  }

  function newPassword(u: AdminUser) {
    if (!window.confirm(`Reimpostare la password di ${u.name}? Quella attuale smette di funzionare.`)) return;
    return act(u.id, async () => {
      const r = await call<{ username: string; password: string }>(token, `/api/admin/users/${encodeURIComponent(u.id)}/password`, "POST", {});
      setIssued({ title: `Nuova password di ${u.name}`, username: r.username, password: r.password });
    });
  }

  function rename(u: AdminUser) {
    const name = window.prompt("Nome e cognome", u.name)?.trim();
    if (!name || name === u.name) return;
    void update(u.id, { name });
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!nName.trim() || !nUser.trim()) { setError("Nome e username sono obbligatori"); return; }
    await act("new", async () => {
      const r = await call<{ user: AdminUser; password: string }>(token, "/api/admin/users", "POST",
        { name: nName.trim(), department: nDept.trim(), role: nRole, username: nUser.trim(), password: nPwd || undefined, isAdmin: nAdmin });
      setUsers((us) => [...(us ?? []), r.user]);
      setIssued({ title: `Account creato: ${r.user.name}`, username: r.user.username ?? "", password: r.password });
      setNName(""); setNDept(""); setNUser(""); setNPwd(""); setNAdmin(false); setShowNew(false);
    });
  }

  const q = filter.trim().toLowerCase();
  const shown = (users ?? [])
    .filter((u) => !q || u.name.toLowerCase().includes(q) || (u.username ?? "").toLowerCase().includes(q) || u.department.toLowerCase().includes(q))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.role.localeCompare(b.role) || a.name.localeCompare(b.name, "it"));

  return (
    <section className="card history admin">
      <h2>Utenti <button className="ghost" onClick={onBack}>← Indietro</button></h2>
      <p className="muted">Chi dimentica la password la chiede a un amministratore: "Nuova password" ne genera una da consegnare all'utente. Gli account provenienti dalla rubrica SIA restano allineati nel nome; la password è locale.</p>
      <div className="row admin-bar">
        <input className="filter" placeholder="Cerca per nome, username o reparto" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className="primary" onClick={() => setShowNew((v) => !v)}>{showNew ? "Annulla" : "+ Nuovo account"}</button>
        <button className="ghost" onClick={reload} title="Ricarica">↻</button>
      </div>
      {error && <p className="login-error">{error}</p>}

      {showNew && (
        <form className="card new-user" onSubmit={create}>
          <div className="grid2">
            <label>Nome e cognome<input value={nName} onChange={(e) => setNName(e.target.value)} autoFocus required /></label>
            <label>Reparto<input value={nDept} onChange={(e) => setNDept(e.target.value)} /></label>
            <label>Tipo di account
              <select value={nRole} onChange={(e) => setNRole(e.target.value as AdminUser["role"])}>
                <option value="expert">Esperto (app web)</option>
                <option value="technician">Utente HoloLens (visore)</option>
              </select>
            </label>
            <label>Username<input value={nUser} onChange={(e) => setNUser(e.target.value)} autoComplete="off" required /></label>
            <label>Password <small>(vuota = generata)</small><input type="text" value={nPwd} onChange={(e) => setNPwd(e.target.value)} autoComplete="off" /></label>
            <label className="check"><input type="checkbox" checked={nAdmin} onChange={(e) => setNAdmin(e.target.checked)} /><span>Amministratore (vede questa pagina)</span></label>
          </div>
          <div className="row">
            <button className="primary" type="submit" disabled={busy === "new"}>{busy === "new" ? "Creazione…" : "Crea account"}</button>
          </div>
        </form>
      )}

      {issued && (
        <div className="card issued">
          <h3>{issued.title}</h3>
          <p>Comunica queste credenziali all'utente: la password non sarà più visibile.</p>
          <p className="cred">username <code>{issued.username}</code> · password <code>{issued.password}</code></p>
          <div className="row">
            <button className="ghost" onClick={() => navigator.clipboard?.writeText(`${issued.username} / ${issued.password}`).catch(() => {})}>Copia</button>
            <button className="primary" onClick={() => setIssued(null)}>Ho preso nota</button>
          </div>
        </div>
      )}

      {users && shown.length === 0 && <p className="muted">Nessun account.</p>}
      {shown.length > 0 && (
        <table className="history-table">
          <thead>
            <tr><th>Nome</th><th>Tipo</th><th>Username</th><th>Reparto</th><th>Stato</th><th>Azioni</th></tr>
          </thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} className={u.active ? "" : "inactive"}>
                <td data-label="Nome"><b>{u.name}</b>{u.isAdmin && <small> · amministratore</small>}{u.fromPredimain && <small> · SIA</small>}{u.id === meId && <small> · tu</small>}</td>
                <td data-label="Tipo">{roleText(u.role)}</td>
                <td data-label="Username">{u.username ?? <span className="muted">chiave API</span>}</td>
                <td data-label="Reparto">{u.department || "–"}</td>
                <td data-label="Stato">{u.active ? "attivo" : "disattivato"}</td>
                <td data-label="Azioni" className="actions">
                  <button className="ghost" disabled={busy === u.id || !u.username} onClick={() => newPassword(u)}>Nuova password</button>
                  <button className="ghost" disabled={busy === u.id} onClick={() => rename(u)}>Rinomina</button>
                  <button className="ghost" disabled={busy === u.id || u.id === meId} onClick={() => update(u.id, { isAdmin: !u.isAdmin })}>{u.isAdmin ? "Togli admin" : "Rendi admin"}</button>
                  <button className={u.active ? "ghost warn" : "ghost"} disabled={busy === u.id || u.id === meId}
                    onClick={() => { if (!u.active || window.confirm(`Disattivare ${u.name}? Non potrà più accedere né essere chiamato.`)) void update(u.id, { active: !u.active }); }}>
                    {u.active ? "Disattiva" : "Riattiva"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
